import "@/App.css";
import AppNotifications from "@/components/AppNotifications";
import { NavigationProvider } from "@/navigation/NavigationProvider";
import "@/styles/spreadsheet-grid.css";
import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, RouterProvider, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { UnsavedChangesProvider } from "@/navigation/UnsavedChangesProvider";
import ProtectedRoute from "@/auth/ProtectedRoute";
import GuardedRoute from "@/auth/GuardedRoute";
import LoginPage from "@/pages/LoginPage";
import ExceptionDesk from "@/pages/ExceptionDesk";
import ProjectAcceptancePage from "@/pages/ProjectAcceptancePage";
import ActiveJobWorkspacePage from "@/pages/ActiveJobWorkspacePage";
import MasterBoardPage from "@/pages/MasterBoardPage";
import ManagerBoard from "@/pages/ManagerBoard";
import ClosedProjectsPage from "@/pages/ClosedProjectsPage";
import DeletedProjectsPage from "@/pages/DeletedProjectsPage";
import InvoiceWorkspace from "@/pages/InvoiceWorkspace";
import PlatformCompaniesPage from "@/pages/PlatformCompaniesPage";
import ActionAiAssistantPage from "@/pages/ActionAiAssistantPage";
import ProductionTrackerPage from "@/pages/ProductionTrackerPage";
import PasswordChangePage from "@/pages/PasswordChangePage";
import PlatformOwnerRecoveryPage from "@/pages/PlatformOwnerRecoveryPage";
import UserManagementPage from "@/pages/UserManagementPage";
import ActionQueuePage from "@/pages/ActionQueuePage";
import CommunicationLogWorkspacePage from "@/pages/CommunicationLogWorkspacePage";
import PayrollPage from "@/pages/PayrollPage";
import PermitsLocatesPage from "@/pages/PermitsLocatesPage";
import InformationPage from "@/pages/InformationPage";
import BillingPage from "@/pages/BillingPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import AssetsPage from "@/pages/AssetsPage";
import AppointmentsCalendarPage from "@/pages/AppointmentsCalendarPage";
import OwnerLabPage from "@/pages/OwnerLabPage";
import ProjectActivityHistoryPage from "@/pages/ProjectActivityHistoryPage";

import SettingsPage from "@/pages/SettingsPage";
import ImportExportPage from "@/pages/ImportExportPage";
import QueueTimingPage from "@/pages/QueueTimingPage";

function RouteWorkspace() {
  const { user } = useAuth();
  const scope = user ? `${user.id}:${user.company_id}:${user.role}:${user.platform_role}` : "signed-out";
  return <NavigationProvider><UnsavedChangesProvider key={scope}><Outlet key={scope} /></UnsavedChangesProvider></NavigationProvider>;
}

function RouteFailure() {
  return <main className="board-error-state"><h1>This page could not be loaded</h1>
    <p>Reload the page to try again.</p><button type="button" onClick={() => window.location.reload()}>Reload page</button></main>;
}

const router = createBrowserRouter(createRoutesFromElements(
  <Route element={<RouteWorkspace />} errorElement={<RouteFailure />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/recover-platform-owner" element={<PlatformOwnerRecoveryPage />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<Navigate replace to="/dashboard" />} />
              <Route path="/dashboard" element={<ExceptionDesk />} />
              <Route path="/ai-assistant" element={<GuardedRoute permission="ai_assistant:use" feature="ai_assistant"><ActionAiAssistantPage /></GuardedRoute>} />
              <Route
                path="/projects/:projectId"
                element={<GuardedRoute permission="operations:read"><ProjectAcceptancePage /></GuardedRoute>}
              />
              <Route
                path="/active-jobs/:jobId"
                element={<GuardedRoute permission="operations:read"><ActiveJobWorkspacePage /></GuardedRoute>}
              />
              <Route
                path="/master-project-board"
                element={<MasterBoardPage />}
              />
              <Route path="/manager-board" element={<GuardedRoute blockRoles={["supervisor"]}><ManagerBoard /></GuardedRoute>} />
              <Route path="/closed-projects" element={<GuardedRoute blockRoles={["supervisor"]}><ClosedProjectsPage /></GuardedRoute>} />
              <Route path="/deleted-projects" element={<GuardedRoute roles={["owner_admin"]}><DeletedProjectsPage /></GuardedRoute>} />
              <Route path="/invoices" element={<GuardedRoute permission="billing:read" feature="billing"><InvoiceWorkspace /></GuardedRoute>} />
              <Route
                path="/platform/companies"
                element={<PlatformCompaniesPage />}
              />
              <Route
                path="/platform/production-health"
                element={<PlatformCompaniesPage initialTab="runtime-health" />}
              />
              <Route
                path="/platform/guardian"
                element={<PlatformCompaniesPage initialTab="guardian" />}
              />
              <Route
                path="/platform/access-studio"
                element={<PlatformCompaniesPage initialTab="access-studio" />}
              />
              <Route
                path="/platform/security"
                element={<PlatformCompaniesPage initialTab="security" />}
              />
              <Route path="/production" element={<GuardedRoute blockRoles={["supervisor", "project_manager"]} feature="production_tracker"><ProductionTrackerPage /></GuardedRoute>} />
              <Route path="/change-password" element={<PasswordChangePage />} />
              <Route path="/users" element={<GuardedRoute permission="users:manage"><Navigate replace to="/settings/users" /></GuardedRoute>} />
              <Route path="/settings/users" element={<GuardedRoute permission="users:manage"><UserManagementPage /></GuardedRoute>} />
              <Route path="/my-work" element={<Navigate replace to="/master-project-board" />} />
              <Route path="/action-queue" element={<GuardedRoute blockRoles={["supervisor"]}><ActionQueuePage /></GuardedRoute>} />
              <Route path="/project-activity" element={<GuardedRoute blockRoles={["supervisor"]}><Navigate replace to="/settings/activity-history" /></GuardedRoute>} />
              <Route path="/settings/activity-history" element={<GuardedRoute blockRoles={["supervisor"]}><ProjectActivityHistoryPage /></GuardedRoute>} />
              <Route path="/workspace" element={<Navigate replace to="/dashboard" />} />
              <Route path="/communications" element={<GuardedRoute feature="communication_log"><CommunicationLogWorkspacePage /></GuardedRoute>} />
              <Route path="/payroll" element={<GuardedRoute permission="payroll:manage" feature="payroll"><PayrollPage /></GuardedRoute>} />
              <Route path="/permits-locates" element={<GuardedRoute blockRoles={["supervisor"]} anyFeatures={["permits", "locates"]}><PermitsLocatesPage /></GuardedRoute>} />
              <Route path="/information" element={<GuardedRoute roles={["owner_admin", "office_admin"]}><InformationPage /></GuardedRoute>} />
              <Route path="/billing" element={<GuardedRoute permission="billing:read" feature="billing"><BillingPage /></GuardedRoute>} />
              <Route path="/analytics" element={<GuardedRoute permission="analytics:read" feature="analytics"><AnalyticsPage /></GuardedRoute>} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/import-export" element={<GuardedRoute roles={["owner_admin", "office_admin"]} permission="imports:manage"><ImportExportPage /></GuardedRoute>} />
              <Route path="/settings/queue-timing" element={<GuardedRoute roles={["owner_admin", "office_admin"]} permission="imports:manage"><QueueTimingPage /></GuardedRoute>} />
              <Route path="/assets" element={<GuardedRoute permission="assets:manage"><AssetsPage /></GuardedRoute>} />
              <Route path="/appointments" element={<GuardedRoute blockRoles={["supervisor"]}><AppointmentsCalendarPage /></GuardedRoute>} />
              <Route path="/schedule" element={<Navigate replace to="/master-project-board" />} />
              <Route path="/owner-lab" element={<GuardedRoute roles={["owner_admin"]} feature="owner_lab"><OwnerLabPage /></GuardedRoute>} />
              <Route path="/architecture" element={<Navigate replace to="/dashboard" />} />
              <Route path="*" element={<Navigate replace to="/dashboard" />} />
            </Route>
  </Route>
));

function App() {
  return <AuthProvider><div className="App"><AppNotifications /><RouterProvider router={router} /></div></AuthProvider>;
}

export default App;



